/**
 * @description
 * Environment detection utility for determining development vs production mode.
 * Provides consistent environment detection across frontend and backend.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 9, 2025
 */

/**
 * Environment types
 */
export type Environment = 'development' | 'production' | 'preview';

/**
 * Check if running in development mode
 * Uses multiple detection methods for reliability
 */
export function isDevelopment(): boolean {
  // Vite development mode detection
  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    return true;
  }

  // Vite mode detection
  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'development') {
    return true;
  }

  // Node environment detection
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
    return true;
  }

  // Browser hostname detection
  if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
    return true;
  }

  // Browser hostname detection (alternative ports)
  if (typeof window !== 'undefined' && window.location?.hostname === '127.0.0.1') {
    return true;
  }

  // Environment variable detection
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ENVIRONMENT === 'development') {
    return true;
  }

  return false;
}

/**
 * Check if running in production mode
 */
export function isProduction(): boolean {
  // Vite production mode detection
  if (typeof import.meta !== 'undefined' && import.meta.env?.PROD) {
    return true;
  }

  // Vite mode detection
  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'production') {
    return true;
  }

  // Node environment detection
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    return true;
  }

  // Environment variable detection
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ENVIRONMENT === 'production') {
    return true;
  }

  return false;
}

/**
 * Check if running in preview mode
 */
export function isPreview(): boolean {
  // Vite mode detection
  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'preview') {
    return true;
  }

  // Environment variable detection
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ENVIRONMENT === 'preview') {
    return true;
  }

  return false;
}

/**
 * Get current environment
 */
export function getEnvironment(): Environment {
  if (isDevelopment()) return 'development';
  if (isPreview()) return 'preview';
  if (isProduction()) return 'production';
  
  // Default to production for safety
  return 'production';
}

/**
 * Environment constants for easy access
 */
export const IS_DEV = isDevelopment();
export const IS_PROD = isProduction();
export const IS_PREVIEW = isPreview();
export const CURRENT_ENV = getEnvironment();

/**
 * Debug information about environment detection
 */
export function getEnvironmentDebugInfo() {
  return {
    environment: getEnvironment(),
    isDev: IS_DEV,
    isProd: IS_PROD,
    isPreview: IS_PREVIEW,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
    viteDev: typeof import.meta !== 'undefined' ? import.meta.env?.DEV : 'N/A',
    viteMode: typeof import.meta !== 'undefined' ? import.meta.env?.MODE : 'N/A',
    viteEnv: typeof import.meta !== 'undefined' ? import.meta.env?.VITE_ENVIRONMENT : 'N/A',
    nodeEnv: typeof process !== 'undefined' ? process.env?.NODE_ENV : 'N/A',
  };
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
